import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select, useToast } from '../components/common';
import api from '../services/api';
import { cliApiBaseFlag } from '../utils/cliApiBase';
import { copyText } from '../utils/clipboard';
import PortalPage from '../components/PortalPage';
import './CliPage.css';

/**
 * Everything needed to start working from a terminal, and what to type once you are.
 *
 * The useful half is the top: a session block already carrying this developer's
 * app id and API base. Tokens are made under Develop → Tokens, where each one is
 * named for the machine it lives on — this page never mints one, because a token
 * auto-named by date is a ledger nobody can read.
 */
function CliPage({ embedded = false }) {
  const toast = useToast();
  const [apps, setApps] = useState([]);
  const [appId, setAppId] = useState('');

  useEffect(() => {
    api.request('/third-party/my-apps')
      .then(r => setApps(r.apps || []))
      .catch(() => setApps([]));
  }, []);

  const app = apps.find(a => a.id === appId);
  const apiBaseFlag = useMemo(() => cliApiBaseFlag(api.getBaseUrl?.()), []);

  const copy = useCallback(async (text, what) => {
    if (await copyText(text)) toast.success(`${what} copied.`);
    else toast.error('Could not copy. Select the text and copy it by hand.');
  }, [toast]);

  const setup = useMemo(() => {
    const lines = [`paradigm login --token <paste a token>${apiBaseFlag}`];
    if (appId) {
      lines.push('', '# bind this directory to the app (gitignored)',
        'mkdir -p .paradigm', `printf '[app]\\napp_id = "${appId}"\\n' > .paradigm/secrets.toml`);
    }
    return lines.join('\n');
  }, [appId, apiBaseFlag]);

  const primer = useMemo(() => {
    const name = app?.name || 'my app';
    return [
      `I'm working on "${name}", an ofSelf app. ofSelf is an identity graph: people own their`,
      `data and apps request scoped access to it.`,
      '',
      `The paradigm CLI is authenticated in this directory and bound to app ${appId || '<app-id>'}.`,
      `The app's permission request (its DLR) lives on the server, not in a file — read it with`,
      '`paradigm dlr show`. With a CRUX, change it in \u00a79 of CRUX.md and run `paradigm crux sync`;',
      'without one, use `paradigm dlr add-read <schema>` and friends.',
      '',
      'Before inventing a schema, search for one that already exists:',
      '`paradigm schema search <term> --json`. Reuse is the point — a custom schema is data',
      'only your app can read.',
      '',
      'Start by running `paradigm dlr show` and `paradigm crux check`, and tell me what the app',
      'currently asks for and whether its design doc justifies it.',
    ].join('\n');
  }, [app, appId]);

  // Rendered on its own route, this is a page. Rendered as a mode of "Start
  // developing", the surrounding page already carries the title — a second one
  // here would repeat it.
  const Frame = embedded ? EmbeddedFrame : PortalPage;

  return (
    <Frame
      className="cli"
      title="Working from the terminal"
      lede="The CLI is how apps get built — and the only path an agent can drive. The portal and the CLI talk to the same records, so a change made in either shows up in the other."
    >
      <ol className="cli__steps">
        <Step n="1" title="Install">
          <Paste
            text="pip install 'paradigm-cli @ git+https://github.com/Radius-of-Self/paradigm_sdk.git@main#subdirectory=cli'"
            onCopy={copy} label="Install command"
          />
        </Step>

        <Step n="2" title="Start a session">
          <p className="cli__note">
            You need a developer token — make one under{' '}
            <Link to="/tokens" className="cli__link">Develop → Tokens</Link>, named for this
            machine. It is shown once; paste it in place of the placeholder.
          </p>
          <label className="cli__label">
            <span>App to bind this directory to</span>
            <Select value={appId} onChange={e => setAppId(e.target.value)}>
              <option value="">— choose one —</option>
              {apps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </label>
          <Paste text={setup} onCopy={copy} label="Setup" testId="cli-setup" />
        </Step>

        <Step n="3" title="Hand it to an agent">
          <p className="cli__note">
            Paste this into Claude Code after the setup above. It says what ofSelf is, where this
            app's permissions live, and to look for an existing schema before inventing one.
          </p>
          <Paste text={primer} onCopy={copy} label="Primer" />
        </Step>
      </ol>

      <section className="cli__ref">
        <h2 className="cli__ref-title">Commands</h2>
        <p className="cli__note">Grouped by what you are trying to do, not alphabetically.</p>
        {COMMAND_GROUPS.map(g => (
          <div key={g.title} className="cli__group">
            <h3>{g.title}</h3>
            <p className="cli__group-why">{g.why}</p>
            <table className="cli__table">
              <tbody>
                {g.commands.map(c => (
                  <tr key={c.cmd}>
                    <td className="cli__cmd">
                      <code>{c.cmd}</code>
                      <button
                        type="button" className="cli__copy"
                        onClick={() => copy(c.cmd, 'Command')} aria-label={`Copy ${c.cmd}`}
                      >Copy</button>
                    </td>
                    <td className="cli__what">{c.what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </section>
    </Frame>
  );
}

/** The same content with no page chrome, for use inside another page. */
function EmbeddedFrame({ className, children }) {
  return <div className={className}>{children}</div>;
}

function Step({ n, title, children }) {
  return (
    <li className="cli__step">
      <span className="cli__step-n">{n}</span>
      <div className="cli__step-main">
        <h3>{title}</h3>
        {children}
      </div>
    </li>
  );
}

function Paste({ text, onCopy, label, testId }) {
  return (
    <div className="cli__paste">
      <pre data-testid={testId}>{text}</pre>
      <Button size="sm" className="cli__paste-copy" onClick={() => onCopy(text, label)}>Copy</Button>
    </div>
  );
}

const COMMAND_GROUPS = [
  {
    title: 'Signing in',
    why: 'A developer token is a control-plane credential. It manages apps and schemas and is refused on anything holding personal data.',
    commands: [
      { cmd: 'paradigm login --token <token>', what: 'Store credentials for this machine.' },
      { cmd: 'paradigm whoami', what: 'Who you are, which backend, personal or group context.' },
      { cmd: 'paradigm token create --name <name>', what: 'A new token. Printed once.' },
      { cmd: 'paradigm token list', what: 'Tokens you hold, and when they expire.' },
      { cmd: 'paradigm context set', what: 'Work as a group rather than yourself.' },
    ],
  },
  {
    title: 'Starting an app',
    why: 'A CRUX comes before code: it decides which schemas the app reuses, and §9 becomes the consent screen people actually see.',
    commands: [
      { cmd: 'paradigm init --name "My App"', what: 'Scaffold a project.' },
      { cmd: 'paradigm crux init', what: 'Create the design doc.' },
      { cmd: 'paradigm crux questions --json', what: 'The interview that fills it, anchored to the real schema registry.' },
      { cmd: 'paradigm crux sync', what: 'Push \u00a79 of CRUX.md to the app\u2019s DLR.' },
      { cmd: 'paradigm crux check', what: 'Which sections are still unwritten.' },
      { cmd: 'paradigm crux push', what: 'Store it on the app, versioned with the DLR.' },
      { cmd: 'paradigm app push -m "initial"', what: 'Register or update the app.' },
    ],
  },
  {
    title: 'Finding schemas',
    why: 'Reuse is the whole point. A custom schema is data only your app can read, so it should be a last resort.',
    commands: [
      { cmd: 'paradigm schema search <term> --json', what: 'Look before inventing.' },
      { cmd: 'paradigm schema list --limit 100 --json', what: 'Everything in the registry.' },
      { cmd: 'paradigm schema show <uri> --json', what: 'Fields, encryption, parent schema.' },
      { cmd: 'paradigm schema push --file ./schema.yaml', what: 'Add one, if nothing fits.' },
    ],
  },
  {
    title: 'What the app asks for',
    why: 'The DLR lives on the app record, so these read and write the same thing the portal edits. In a project with a CRUX, the add/remove commands refuse — edit \u00a79 of CRUX.md and run paradigm crux sync, so the design and the contract cannot drift apart.',
    commands: [
      { cmd: 'paradigm dlr show', what: 'What the app currently requests.' },
      { cmd: 'paradigm dlr add-read <schema>', what: 'Request read access.' },
      { cmd: 'paradigm dlr add-write <schema>', what: 'Request create and edit.' },
      { cmd: 'paradigm dlr add-verb activity:read', what: 'A bare verb, no schema.' },
      { cmd: 'paradigm dlr remove <schema-or-verb>', what: 'Stop asking for something.' },
      { cmd: 'paradigm dlr preview', what: 'The consent card a person will see.' },
      { cmd: 'paradigm dlr diff', what: 'What changed since an earlier version.' },
    ],
  },
  {
    title: 'Shipping and checking',
    why: 'Every save snapshots the whole spec, so a change can be traced — and undone.',
    commands: [
      { cmd: 'paradigm validate', what: 'Structure, verbs, and whether the schemas resolve.' },
      { cmd: 'paradigm app list', what: 'Your apps.' },
      { cmd: 'paradigm app show', what: 'One app in full.' },
      { cmd: 'paradigm app versions', what: 'History.' },
      { cmd: 'paradigm app rollback <n>', what: 'Restore an earlier spec.' },
      { cmd: 'paradigm app metrics --days 30', what: 'Calls, users, latency.' },
      { cmd: 'paradigm app test-users create', what: 'A pre-authorized user, so you can build without the consent round-trip.' },
    ],
  },
];

export default CliPage;
